import React, { useState, useEffect, useRef } from "react";
import {
  HiOutlineGlobeAlt,
  HiListBullet,
  HiOutlineSparkles,
  HiOutlineMagnifyingGlass,
  HiArrowPath,
  HiOutlineXMark,
  HiOutlineDocumentText,
  HiOutlineShoppingBag,
  HiOutlinePlayCircle,
  HiCheck,
  HiOutlineMapPin,
  HiOutlineCircleStack,
  HiChevronLeft,
  HiChevronRight,
  HiHeart,
  HiOutlineHeart,
} from "react-icons/hi2";
import { FiDownload } from "react-icons/fi";
import { useLanguage } from "../contexts/LanguageContext";
import { foodBg } from "../assets/images";
import {
  surface,
  surfaceMuted,
  borderSub,
  textPrimary,
  textSecondary,
  roundedPanel,
} from "../components/layout/UserLayout/designTokens";
import { translateBatch } from "../utils/translationAPI";
import {
  specialCuisines,
  countryRegions,
} from "./WorldMenus/data/worldMenusData";
import { MEALS_PER_PAGE, GLASS_PANEL } from "./WorldMenus/constants";
import { downloadRecipePdf } from "../utils/recipePdf.js";
import { CardGridLoading } from "../components/common/CardGridLoading";

/**
 * Page des Menus du Monde
 * Affiche des recettes du monde entier via TheMealDB API
 */
const WorldMenus = () => {
  const {
    t,
    language,
    translateRecipeName,
    translateIngredient,
    translateCategory,
    translateInstructions,
  } = useLanguage();

  const [meals, setMeals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [translatedMealsMap, setTranslatedMealsMap] = useState({});
  const [translatedSelected, setTranslatedSelected] = useState(null);
  const [translatedCuisineNames, setTranslatedCuisineNames] = useState({});
  const [translatedCategoriesMap, setTranslatedCategoriesMap] = useState({});
  const [translatedCountriesMap, setTranslatedCountriesMap] = useState({});
  const [filterTab, setFilterTab] = useState("categories"); // "categories" | "countries" | "special"
  const [page, setPage] = useState(1);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchSuggestionsRef = useRef(null);
  const searchDropdownBlurTimer = useRef(null);
  const translateGridRef = useRef(0);
  const translateModalRef = useRef(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [translatingModal, setTranslatingModal] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [favoriteMeals, setFavoriteMeals] = useState(() => {
    try {
      const stored = localStorage.getItem("worldMenusFavorites");
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Charger les catégories et pays au démarrage
  useEffect(() => {
    fetchCategories();
    fetchCountries();
    fetchRandomMeals();
  }, []);

  // Traduire les noms/catégories/zones des repas de la grille via l'API
  useEffect(() => {
    if (!meals.length || !language) {
      setTranslatedMealsMap({});
      return;
    }
    const runId = ++translateGridRef.current;
    const texts = meals.flatMap((m) => [
      m.strMeal || "",
      m.strCategory || "",
      m.strArea || "",
    ]);
    translateBatch(texts, language, "auto").then((results) => {
      if (runId !== translateGridRef.current) return;
      const next = {};
      meals.forEach((m, i) => {
        next[m.idMeal] = {
          strMeal: results[i * 3] || m.strMeal,
          strCategory: results[i * 3 + 1] || m.strCategory,
          strArea: results[i * 3 + 2] || m.strArea,
        };
      });
      setTranslatedMealsMap(next);
    });
    return () => {};
  }, [meals, language]);

  // Traduire les noms des cuisines spéciales (sidebar)
  useEffect(() => {
    const names = specialCuisines.map((c) => c.name);
    if (!names.length || !language) {
      setTranslatedCuisineNames({});
      return;
    }
    translateBatch(names, language, "auto").then((results) => {
      const next = {};
      specialCuisines.forEach((c, i) => {
        next[c.name] = results[i] ?? c.name;
      });
      setTranslatedCuisineNames(next);
    });
  }, [language]);

  // Traduire les noms des catégories (sidebar)
  useEffect(() => {
    const list = categories.slice(0, 10).map((c) => c.strCategory);
    if (!list.length || !language) {
      setTranslatedCategoriesMap({});
      return;
    }
    translateBatch(list, language, "auto").then((results) => {
      const next = {};
      categories.slice(0, 10).forEach((c, i) => {
        next[c.strCategory] = results[i] ?? c.strCategory;
      });
      setTranslatedCategoriesMap(next);
    });
  }, [categories, language]);

  // Traduire les noms des pays (sidebar)
  useEffect(() => {
    const list = countries.map((c) => c.strArea);
    if (!list.length || !language) {
      setTranslatedCountriesMap({});
      return;
    }
    translateBatch(list, language, "auto").then((results) => {
      const next = {};
      countries.forEach((c, i) => {
        next[c.strArea] = results[i] ?? c.strArea;
      });
      setTranslatedCountriesMap(next);
    });
  }, [countries, language]);

  // Traduire le contenu de la modal (titre, zone, catégorie, instructions, ingrédients + mesures)
  useEffect(() => {
    if (!selectedMeal || !language) {
      setTranslatedSelected(null);
      setTranslatingModal(false);
      return;
    }
    setTranslatingModal(true);
    const runId = ++translateModalRef.current;
    const m = selectedMeal;
    const ingredientTexts = [];
    const measureTexts = [];
    for (let i = 1; i <= 20; i++) {
      const ing = m[`strIngredient${i}`];
      if (ing && ing.trim()) {
        ingredientTexts.push(ing.trim());
        measureTexts.push((m[`strMeasure${i}`] || "").trim());
      }
    }
    // Découper les instructions par lignes pour traduire chaque étape séparément
    const instructionLines = (m.strInstructions || "")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    const allTexts = [
      m.strMeal,
      m.strCategory,
      m.strArea,
      ...instructionLines,
      ...ingredientTexts,
      ...measureTexts,
    ];
    translateBatch(allTexts, language, "auto")
      .then((results) => {
        if (runId !== translateModalRef.current) return;
        setTranslatingModal(false);
        const n = ingredientTexts.length;
        const nInst = instructionLines.length;
        const [strMeal, strCategory, strArea, ...instTrans] = results;
        const instResults = instTrans.slice(0, nInst);
        const ingTrans = instTrans.slice(nInst, nInst + n);
        const measureTrans = instTrans.slice(nInst + n, nInst + n + n);
        const strInstructions = instructionLines
          .map((line, i) => instResults[i] ?? line)
          .join("\n\n");
        const ingredients = ingredientTexts.map((name, idx) => ({
          name: ingTrans[idx] ?? name,
          measure: (
            measureTrans[idx] ??
            measureTexts[idx] ??
            m[`strMeasure${idx + 1}`] ??
            ""
          ).trim(),
        }));
        setTranslatedSelected({
          strMeal: strMeal || m.strMeal,
          strCategory: strCategory || m.strCategory,
          strArea: strArea || m.strArea,
          strInstructions: strInstructions || m.strInstructions || "",
          ingredients,
        });
      })
      .catch(() => {
        if (runId === translateModalRef.current) setTranslatingModal(false);
      });
    return () => {};
  }, [selectedMeal, language]);

  const handleDownloadSelectedPdf = async () => {
    if (!selectedMeal) return;
    try {
      setPdfLoading(true);

      const ingredientsList =
        translatedSelected?.ingredients?.map((ing) =>
          `${ing.measure} ${ing.name}`.trim(),
        ) ||
        Array.from({ length: 20 }, (_, i) => i + 1)
          .map((i) => {
            const ing = selectedMeal[`strIngredient${i}`];
            const measure = selectedMeal[`strMeasure${i}`];
            return ing && ing.trim()
              ? `${(measure || "").trim()} ${ing.trim()}`.trim()
              : null;
          })
          .filter(Boolean);

      const instructionsText =
        translatedSelected?.strInstructions ||
        selectedMeal.strInstructions ||
        "";
      const instructionsList = instructionsText
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);

      const recipeLike = {
        title:
          translatedSelected?.strMeal ??
          translateRecipeName(selectedMeal.strMeal),
        description: [
          translatedSelected?.strCategory ??
            translateCategory(selectedMeal.strCategory),
          translatedSelected?.strArea ?? selectedMeal.strArea,
        ]
          .filter(Boolean)
          .join(" • "),
        ingredients: ingredientsList.join("\n"),
        instructions: instructionsList.join("\n"),
        image_url: selectedMeal.strMealThumb,
        prep_time: null,
        cook_time: null,
        servings: null,
        difficulty: "",
        category:
          translatedSelected?.strCategory ?? selectedMeal.strCategory ?? "",
        area: translatedSelected?.strArea ?? selectedMeal.strArea ?? "",
      };

      await downloadRecipePdf(recipeLike, {
        ingredientsList,
        instructionsList,
        labels: {
          prep: t("preparation"),
          cook: t("cooking"),
          servings: t("servings"),
          difficulty: t("difficulty") || "Difficulté",
          ingredients: t("ingredients"),
          instructions: t("instructions"),
        },
      });
    } finally {
      setPdfLoading(false);
    }
  };

  // Charger les catégories
  const fetchCategories = async () => {
    try {
      const response = await fetch(
        "https://www.themealdb.com/api/json/v1/1/categories.php",
      );
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error("Erreur lors du chargement des catégories:", error);
    }
  };

  // Charger les pays
  const fetchCountries = async () => {
    try {
      const response = await fetch(
        "https://www.themealdb.com/api/json/v1/1/list.php?a=list",
      );
      const data = await response.json();
      // Filtrer pour exclure la Thaïlande
      let filteredCountries = (data.meals || []).filter(
        (country) => country.strArea !== "Thai",
      );

      // Ajouter manuellement le Cambodge comme pays disponible
      const hasCambodian = filteredCountries.some(
        (country) => country.strArea === "Cambodian",
      );
      if (!hasCambodian) {
        filteredCountries = [...filteredCountries, { strArea: "Cambodian" }];
      }

      setCountries(filteredCountries);
    } catch (error) {
      console.error("Erreur lors du chargement des pays:", error);
    }
  };

  // Charger des repas aléatoires
  const fetchRandomMeals = async () => {
    setLoading(true);
    try {
      // D'abord récupérer quelques recettes personnalisées
      let customMeals = [];
      specialCuisines.forEach((cuisine) => {
        if (cuisine.customRecipes && cuisine.customRecipes.length > 0) {
          // Prendre 1-2 recettes au hasard de chaque cuisine
          const randomRecipes = cuisine.customRecipes
            .sort(() => 0.5 - Math.random())
            .slice(0, Math.floor(Math.random() * 2) + 1);
          customMeals = [...customMeals, ...randomRecipes];
        }
      });

      // Ensuite récupérer des recettes API (moins pour laisser de la place aux personnalisées)
      const apiCount = Math.max(8, 12 - customMeals.length);
      const promises = Array(apiCount)
        .fill(null)
        .map(() =>
          fetch("https://www.themealdb.com/api/json/v1/1/random.php").then(
            (res) => res.json(),
          ),
        );
      const results = await Promise.all(promises);
      const apiMeals = results
        .map((result) => result.meals[0])
        .filter(Boolean)
        .filter((meal) => meal.strArea !== "Thai"); // Exclure la Thaïlande

      // Mélanger les recettes personnalisées et API
      const allMeals = [...customMeals, ...apiMeals];
      // Mélanger aléatoirement
      const shuffledMeals = allMeals.sort(() => 0.5 - Math.random());
      setMeals(shuffledMeals);
      setPage(1);
    } catch (error) {
      console.error("Erreur lors du chargement des repas:", error);
    } finally {
      setLoading(false);
    }
  };

  // Récupérer les détails complets d'un meal (pour les résultats filter qui n'ont que idMeal, strMeal, strMealThumb)
  const fetchMealDetails = async (meal) => {
    if (meal.strCategory != null) return meal;
    try {
      const res = await fetch(
        `https://www.themealdb.com/api/json/v1/1/lookup.php?i=${meal.idMeal}`,
      );
      const data = await res.json();
      return data.meals?.[0] || meal;
    } catch {
      return meal;
    }
  };

  const toggleFavoriteMeal = (meal) => {
    const mealId = meal.idMeal;
    setFavoriteMeals((prev) => {
      const next = { ...prev, [mealId]: !prev[mealId] };
      try {
        localStorage.setItem("worldMenusFavorites", JSON.stringify(next));

        // Stocker aussi un résumé des recettes favorites Menus du Monde
        const storedItems =
          JSON.parse(localStorage.getItem("worldMenusFavoriteItems") || "[]") ||
          [];
        let nextItems;
        if (!prev[mealId]) {
          // On vient d'ajouter aux favoris
          const exists = storedItems.some((item) => item.idMeal === mealId);
          const summary = {
            idMeal: meal.idMeal,
            strMeal: meal.strMeal,
            strMealThumb: meal.strMealThumb,
            strArea: meal.strArea || null,
            strCategory: meal.strCategory || null,
          };
          nextItems = exists
            ? storedItems.map((item) =>
                item.idMeal === mealId ? { ...item, ...summary } : item,
              )
            : [...storedItems, summary];
        } else {
          // On vient de retirer des favoris
          nextItems = storedItems.filter((item) => item.idMeal !== mealId);
        }
        localStorage.setItem(
          "worldMenusFavoriteItems",
          JSON.stringify(nextItems),
        );
      } catch {
        // ignore storage errors
      }
      return next;
    });
  };

  const handleDownloadMealPdf = async (meal) => {
    if (!meal) return;
    try {
      setPdfLoading(true);

      let fullMeal = meal;

      // Si c'est une recette personnalisée (khmer, korean, seafood, etc.),
      // reconstruire un objet compatible avec l'API avec ingrédients + instructions
      if (
        typeof meal.idMeal === "string" &&
        (meal.idMeal.startsWith("khmer") ||
          meal.idMeal.startsWith("korean") ||
          meal.idMeal.startsWith("seafood") ||
          meal.idMeal.startsWith("brazilian") ||
          meal.idMeal.startsWith("argentinian"))
      ) {
        const customMeal = specialCuisines
          .flatMap((c) => c.customRecipes || [])
          .find((r) => r.idMeal === meal.idMeal);

        if (customMeal) {
          const mealWithIngredients = {
            ...customMeal,
            strInstructions:
              customMeal.instructions ||
              customMeal.description ||
              "Recette traditionnelle.",
          };

          if (customMeal.ingredients) {
            customMeal.ingredients.forEach((ing, index) => {
              mealWithIngredients[`strIngredient${index + 1}`] = ing.name;
              mealWithIngredients[`strMeasure${index + 1}`] = ing.measure;
            });
          }

          fullMeal = mealWithIngredients;
        }
      } else {
        // Sinon, s'assurer d'avoir tous les détails de la recette depuis l'API
        fullMeal = await fetchMealDetails(meal);
      }

      const ingredientsList = Array.from({ length: 20 }, (_, i) => i + 1)
        .map((i) => {
          const ing = fullMeal[`strIngredient${i}`];
          const measure = fullMeal[`strMeasure${i}`];
          return ing && ing.trim()
            ? `${(measure || "").trim()} ${ing.trim()}`.trim()
            : null;
        })
        .filter(Boolean);

      const instructionsText = fullMeal.strInstructions || "";
      const instructionsList = instructionsText
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);

      const recipeLike = {
        title:
          translatedMealsMap[fullMeal.idMeal]?.strMeal ??
          translateRecipeName(fullMeal.strMeal),
        description: [
          translatedMealsMap[fullMeal.idMeal]?.strCategory ??
            translateCategory(fullMeal.strCategory),
          translatedMealsMap[fullMeal.idMeal]?.strArea ?? fullMeal.strArea,
        ]
          .filter(Boolean)
          .join(" • "),
        ingredients: ingredientsList.join("\n"),
        instructions: instructionsList.join("\n"),
        image_url: fullMeal.strMealThumb,
        prep_time: null,
        cook_time: null,
        servings: null,
        difficulty: "",
        category:
          translatedMealsMap[fullMeal.idMeal]?.strCategory ??
          fullMeal.strCategory ??
          "",
        area:
          translatedMealsMap[fullMeal.idMeal]?.strArea ??
          fullMeal.strArea ??
          "",
      };

      await downloadRecipePdf(recipeLike, {
        ingredientsList,
        instructionsList,
        labels: {
          prep: t("preparation"),
          cook: t("cooking"),
          servings: t("servings"),
          difficulty: t("difficulty") || "Difficulté",
          ingredients: t("ingredients"),
          instructions: t("instructions"),
        },
      });
    } finally {
      setPdfLoading(false);
    }
  };

  // Suggestions : recherche par nom, ingrédient, catégorie, pays
  useEffect(() => {
    const term = (searchTerm || "").trim().toLowerCase();
    if (term.length < 2) {
      setSearchSuggestions([]);
      setShowSearchDropdown(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const base = "https://www.themealdb.com/api/json/v1/1";
        const promises = [
          fetch(`${base}/search.php?s=${encodeURIComponent(term)}`).then((r) =>
            r.json(),
          ),
          fetch(`${base}/filter.php?i=${encodeURIComponent(term)}`).then((r) =>
            r.json(),
          ),
        ];
        const matchedCategory = (categories || []).find((c) =>
          (c.strCategory || "").toLowerCase().includes(term),
        );
        if (matchedCategory) {
          promises.push(
            fetch(
              `${base}/filter.php?c=${encodeURIComponent(matchedCategory.strCategory)}`,
            ).then((r) => r.json()),
          );
        }
        const matchedArea = (countries || []).find(
          (c) =>
            (c.strArea || "").toLowerCase().includes(term) ||
            term.includes((c.strArea || "").toLowerCase()),
        );
        if (matchedArea) {
          promises.push(
            fetch(
              `${base}/filter.php?a=${encodeURIComponent(matchedArea.strArea)}`,
            ).then((r) => r.json()),
          );
        }
        const results = await Promise.all(promises);
        if (cancelled) return;
        const byId = new Map();
        results.forEach((data) => {
          (data.meals || []).forEach((m) => {
            if (m.strArea === "Thai") return;
            if (!byId.has(m.idMeal)) byId.set(m.idMeal, m);
          });
        });
        specialCuisines.forEach((cuisine) => {
          const nameMatch = (cuisine.name || "").toLowerCase().includes(term);
          const keywordMatch = (cuisine.keywords || []).some((k) =>
            k.toLowerCase().includes(term),
          );
          const ingredientMatch = (recipe) =>
            (recipe.ingredients || []).some((ing) =>
              (ing.name || "").toLowerCase().includes(term),
            );
          if (cuisine.customRecipes) {
            cuisine.customRecipes.forEach((m) => {
              const id = m.idMeal || m.strMeal;
              if (byId.has(id)) return;
              const match =
                nameMatch ||
                keywordMatch ||
                (m.strMeal || "").toLowerCase().includes(term) ||
                ingredientMatch(m);
              if (match) byId.set(id, m);
            });
          }
        });
        const list = Array.from(byId.values()).slice(0, 12);
        setSearchSuggestions(list);
        setShowSearchDropdown(list.length > 0);
      } catch {
        if (!cancelled) {
          setSearchSuggestions([]);
          setShowSearchDropdown(false);
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [searchTerm, categories, countries]);

  // Fermer le dropdown au clic extérieur (après un court délai pour laisser le clic sur une suggestion)
  const handleSearchInputBlur = () => {
    searchDropdownBlurTimer.current = setTimeout(() => {
      setShowSearchDropdown(false);
    }, 200);
  };
  const handleSearchInputFocus = () => {
    if (searchDropdownBlurTimer.current) {
      clearTimeout(searchDropdownBlurTimer.current);
      searchDropdownBlurTimer.current = null;
    }
    if (searchSuggestions.length > 0) setShowSearchDropdown(true);
  };

  // Exécuter la recherche : par nom, ingrédient, catégorie, pays et spécialités
  const runSearchWithTerm = async (term) => {
    const q = (term ?? (searchTerm || "")).trim();
    setShowSearchDropdown(false);
    setSearchSuggestions([]);
    if (!q) {
      fetchRandomMeals();
      return;
    }
    setLoading(true);
    try {
      const qLower = q.toLowerCase();
      let customResults = [];
      specialCuisines.forEach((cuisine) => {
        if (cuisine.customRecipes) {
          const nameMatch = (cuisine.name || "").toLowerCase().includes(qLower);
          const keywordMatch = (cuisine.keywords || []).some((k) =>
            k.toLowerCase().includes(qLower),
          );
          const ingredientMatch = (recipe) =>
            (recipe.ingredients || []).some((ing) =>
              (ing.name || "").toLowerCase().includes(qLower),
            );
          const matchingRecipes = cuisine.customRecipes.filter(
            (recipe) =>
              nameMatch ||
              keywordMatch ||
              (recipe.strMeal || "").toLowerCase().includes(qLower) ||
              (recipe.description || "").toLowerCase().includes(qLower) ||
              (recipe.strCategory || "").toLowerCase().includes(qLower) ||
              (recipe.strArea || "").toLowerCase().includes(qLower) ||
              ingredientMatch(recipe),
          );
          customResults = [...customResults, ...matchingRecipes];
        }
      });

      const base = "https://www.themealdb.com/api/json/v1/1";
      const byId = new Map();
      customResults.forEach((m) => byId.set(m.idMeal || m.strMeal, m));

      const searchRes = await fetch(
        `${base}/search.php?s=${encodeURIComponent(q)}`,
      );
      const searchData = await searchRes.json();
      (searchData.meals || [])
        .filter((m) => m.strArea !== "Thai")
        .forEach((m) => byId.set(m.idMeal, m));

      const ingRes = await fetch(
        `${base}/filter.php?i=${encodeURIComponent(q)}`,
      );
      const ingData = await ingRes.json();
      const ingMeals = (ingData.meals || [])
        .filter((m) => m.strArea !== "Thai")
        .slice(0, 10);
      const matchedCategory = (categories || []).find((c) =>
        (c.strCategory || "").toLowerCase().includes(qLower),
      );
      let categoryMeals = [];
      if (matchedCategory) {
        const catRes = await fetch(
          `${base}/filter.php?c=${encodeURIComponent(matchedCategory.strCategory)}`,
        );
        const catData = await catRes.json();
        categoryMeals = (catData.meals || []).slice(0, 8);
      }
      const matchedArea = (countries || []).find(
        (c) =>
          (c.strArea || "").toLowerCase().includes(qLower) ||
          qLower.includes((c.strArea || "").toLowerCase()),
      );
      let areaMeals = [];
      if (matchedArea) {
        const areaRes = await fetch(
          `${base}/filter.php?a=${encodeURIComponent(matchedArea.strArea)}`,
        );
        const areaData = await areaRes.json();
        areaMeals = (areaData.meals || []).slice(0, 8);
      }

      const toEnrich = [...ingMeals, ...categoryMeals, ...areaMeals].filter(
        (m) => !byId.has(m.idMeal) && m.strCategory == null,
      );
      const enriched = await Promise.all(
        toEnrich.map((m) => fetchMealDetails(m)),
      );
      enriched.forEach((m) => byId.set(m.idMeal, m));
      ingMeals.forEach((m) => {
        if (!byId.has(m.idMeal)) byId.set(m.idMeal, m);
      });
      categoryMeals.forEach((m) => {
        if (!byId.has(m.idMeal)) byId.set(m.idMeal, m);
      });
      areaMeals.forEach((m) => {
        if (!byId.has(m.idMeal)) byId.set(m.idMeal, m);
      });

      const allResults = Array.from(byId.values());
      setMeals(allResults);
    } catch (error) {
      console.error("Erreur lors de la recherche:", error);
    } finally {
      setLoading(false);
    }
  };

  const applySuggestion = (meal) => {
    setSearchTerm(meal.strMeal);
    setShowSearchDropdown(false);
    setSearchSuggestions([]);
    runSearchWithTerm(meal.strMeal);
  };

  // Rechercher par nom (soumission du formulaire)
  const handleSearch = async (e) => {
    e.preventDefault();
    runSearchWithTerm(searchTerm);
  };

  // Filtrer par catégorie
  const handleCategoryFilter = async (category) => {
    setSelectedCategory(category);
    setSelectedCountry(""); // Réinitialiser le filtre pays
    setLoading(true);
    try {
      const response = await fetch(
        `https://www.themealdb.com/api/json/v1/1/filter.php?c=${category}`,
      );
      const data = await response.json();

      // Pour les recettes filtrées par catégorie, on doit récupérer les détails pour filtrer par pays
      if (data.meals) {
        const detailedMeals = await Promise.all(
          data.meals.map(async (meal) => {
            const detailResponse = await fetch(
              `https://www.themealdb.com/api/json/v1/1/lookup.php?i=${meal.idMeal}`,
            );
            const detailData = await detailResponse.json();
            return detailData.meals?.[0];
          }),
        );
        // Filtrer pour exclure la Thaïlande
        const filteredMeals = detailedMeals
          .filter(Boolean)
          .filter((meal) => meal.strArea !== "Thai");
        setMeals(filteredMeals);
        setPage(1);
      } else {
        setMeals([]);
        setPage(1);
      }
    } catch (error) {
      console.error("Erreur lors du filtrage:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer par pays
  const handleCountryFilter = async (country) => {
    setSelectedCountry(country);
    setSelectedCategory(""); // Réinitialiser le filtre catégorie
    setLoading(true);

    try {
      // Vérifier si c'est une cuisine spéciale
      const specialCuisine = specialCuisines.find((c) => c.name === country);

      if (specialCuisine) {
        // Commencer avec les recettes personnalisées
        let finalMeals = specialCuisine.customRecipes || [];

        // Compléter avec recherche par mots-clés si nécessaire
        if (specialCuisine.keywords && specialCuisine.keywords.length > 0) {
          const results = await Promise.all(
            specialCuisine.keywords.slice(0, 4).map(async (keyword) => {
              const response = await fetch(
                `https://www.themealdb.com/api/json/v1/1/search.php?s=${keyword}`,
              );
              const data = await response.json();
              return data.meals || [];
            }),
          );

          // Fusionner et dédupliquer les résultats
          const allMeals = results.flat();
          const uniqueMeals = Array.from(
            new Map(allMeals.map((meal) => [meal.idMeal, meal])).values(),
          );

          // Ajouter les recettes API après les recettes personnalisées
          finalMeals = [...finalMeals, ...uniqueMeals.slice(0, 6)];
        }

        setMeals(finalMeals);
        setPage(1);
      } else {
        // Recherche normale par pays
        const response = await fetch(
          `https://www.themealdb.com/api/json/v1/1/filter.php?a=${country}`,
        );
        const data = await response.json();
        setMeals(data.meals || []);
        setPage(1);
      }
    } catch (error) {
      console.error("Erreur lors du filtrage par pays:", error);
    } finally {
      setLoading(false);
    }
  };

  // Afficher les détails d'un repas
  const handleMealClick = async (mealId) => {
    try {
      // Vérifier si c'est une recette personnalisée
      if (
        mealId.startsWith("khmer") ||
        mealId.startsWith("korean") ||
        mealId.startsWith("seafood") ||
        mealId.startsWith("brazilian") ||
        mealId.startsWith("argentinian")
      ) {
        const customMeal = specialCuisines
          .flatMap((c) => c.customRecipes || [])
          .find((r) => r.idMeal === mealId);

        if (customMeal) {
          // Créer un objet compatible avec le format de l'API
          const mealWithIngredients = {
            ...customMeal,
            strInstructions:
              customMeal.instructions ||
              customMeal.description ||
              "Recette traditionnelle.",
          };

          // Ajouter les ingrédients au format API
          if (customMeal.ingredients) {
            customMeal.ingredients.forEach((ing, index) => {
              mealWithIngredients[`strIngredient${index + 1}`] = ing.name;
              mealWithIngredients[`strMeasure${index + 1}`] = ing.measure;
            });
          }

          setSelectedMeal(mealWithIngredients);
          return;
        }
      }

      // Sinon, rechercher dans l'API normale
      const response = await fetch(
        `https://www.themealdb.com/api/json/v1/1/lookup.php?i=${mealId}`,
      );
      const data = await response.json();
      setSelectedMeal(data.meals[0]);
    } catch (error) {
      console.error("Erreur lors du chargement des détails:", error);
    }
  };

  const hasActiveFilter = selectedCategory || selectedCountry || selectedRegion;

  const totalPages = Math.max(1, Math.ceil(meals.length / MEALS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const paginatedMeals = meals.slice(
    (currentPage - 1) * MEALS_PER_PAGE,
    currentPage * MEALS_PER_PAGE,
  );

  const goToPage = (newPage) => {
    const p = Math.max(1, Math.min(totalPages, newPage));
    setPage(p);
  };

  // Remettre à la page 1 si la liste a diminué et que la page courante n'existe plus
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(meals.length / MEALS_PER_PAGE));
    if (page > maxPage) setPage(1);
  }, [meals.length, page]);

  // Filtrage par grandes régions (Asie, Europe, Afrique, Amériques, etc.) pour l'onglet Pays
  const regionLabels = {
    Asia: t("asia") || "Asie",
    Europe: t("europe") || "Europe",
    Africa: t("africa") || "Afrique",
    Americas: t("americas") || "Amériques",
    Oceania: t("oceania") || "Océanie",
    MiddleEast: t("middleEast") || "Moyen-Orient",
    Other: t("other") || "Autres",
  };

  const availableRegions = Array.from(
    new Set(countries.map((c) => countryRegions[c.strArea]).filter(Boolean)),
  );

  const regionFilteredCountries =
    selectedRegion && availableRegions.includes(selectedRegion)
      ? countries.filter((c) => countryRegions[c.strArea] === selectedRegion)
      : countries;

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden">
      {/* Fond identique à la page Recettes */}
      <div
        className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat bg-slate-900"
        style={{ backgroundImage: `url(${foodBg})` }}
        aria-hidden
      />
      <div className="fixed inset-0 z-0 bg-slate-900/40" aria-hidden />

      <div className="relative z-10 w-full min-w-0 py-6 sm:py-8 md:py-10 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* En-tête */}
          <header
            className={`${GLASS_PANEL} p-5 sm:p-6 md:p-8 mb-4 sm:mb-6 overflow-hidden`}
          >
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 text-white flex items-center justify-center gap-2">
                <HiOutlineGlobeAlt className="w-8 h-8 sm:w-9 sm:h-9 text-orange-400 shrink-0" />
                {t("worldMenusTitle")}
              </h1>
              <p className="text-sm sm:text-base text-white/90">
                {t("worldMenusSubtitle")}
              </p>
            </div>
          </header>

          {/* Barre de recherche (z-index au-dessus des filtres quand le dropdown est ouvert) */}
          <section
            className={`${GLASS_PANEL} p-4 sm:p-5 mb-4 sm:mb-6 overflow-visible ${showSearchDropdown ? "relative z-[110]" : ""}`}
          >
            <form onSubmit={handleSearch}>
              <div className="flex flex-col sm:flex-row gap-3">
                <div
                  className="flex-1 min-w-0 relative"
                  ref={searchSuggestionsRef}
                >
                  <HiOutlineMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none z-10" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={handleSearchInputFocus}
                    onBlur={handleSearchInputBlur}
                    placeholder={
                      t("worldMenusSearchPlaceholder") || t("searchPlaceholder")
                    }
                    className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border-2 border-slate-200/80 dark:border-slate-600/80 text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 outline-none transition-colors"
                    autoComplete="off"
                  />
                  {/* Suggestions sous la barre (autocomplete) */}
                  {showSearchDropdown && searchSuggestions.length > 0 && (
                    <div
                      className="absolute left-0 right-0 top-full mt-1 py-1 rounded-xl shadow-xl z-[100] max-h-60 overflow-y-auto border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 [background:white] dark:[background:rgb(30,41,59)]"
                      onMouseDown={(e) => e.preventDefault()}
                      role="listbox"
                      aria-label={
                        t("searchSuggestions") || "Suggestions de recherche"
                      }
                    >
                      {searchSuggestions.map((meal) => (
                        <button
                          key={meal.idMeal}
                          type="button"
                          role="option"
                          className="w-full text-left px-4 py-2.5 text-slate-800 dark:text-slate-100 hover:bg-orange-50 dark:hover:bg-orange-900/20 flex items-center gap-2 transition-colors"
                          onClick={() => applySuggestion(meal)}
                        >
                          <HiOutlineMagnifyingGlass className="w-4 h-4 text-orange-500 shrink-0" />
                          <span className="truncate flex-1 min-w-0">
                            {translatedMealsMap[meal.idMeal]?.strMeal ??
                              meal.strMeal}
                          </span>
                          {(meal.strCategory || meal.strArea) && (
                            <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0 hidden sm:inline">
                              {translatedCategoriesMap[meal.strCategory] ??
                                meal.strCategory}
                              {meal.strArea
                                ? ` · ${translatedCountriesMap[meal.strArea] ?? meal.strArea}`
                                : ""}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  <button
                    type="submit"
                    className="flex-1 sm:flex-none px-5 py-3 rounded-lg font-semibold bg-orange-600 hover:bg-orange-700 text-white transition-colors flex items-center justify-center gap-2"
                  >
                    <HiOutlineMagnifyingGlass className="w-4 h-4" />
                    {t("searchButton")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm("");
                      setSelectedCategory("");
                      setSelectedCountry("");
                      setSelectedRegion("");
                      fetchRandomMeals();
                    }}
                    className="flex-1 sm:flex-none px-4 py-3 rounded-lg font-medium bg-white/20 dark:bg-slate-700/50 text-white border border-white/20 dark:border-slate-600/50 hover:bg-white/30 dark:hover:bg-slate-600/50 transition-colors flex items-center justify-center gap-2"
                  >
                    <HiArrowPath className="w-4 h-4" />
                    {t("resetButton")}
                  </button>
                </div>
              </div>
            </form>
          </section>

          {/* Filtres en onglets - un seul panneau */}
          <section className={`${GLASS_PANEL} p-4 sm:p-5 mb-6 overflow-hidden`}>
            <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
              <div className="flex rounded-lg border border-white/20 dark:border-slate-600 overflow-hidden">
                {[
                  {
                    id: "categories",
                    label: t("categories"),
                    Icon: HiListBullet,
                  },
                  {
                    id: "countries",
                    label: t("countries"),
                    Icon: HiOutlineGlobeAlt,
                  },
                  {
                    id: "special",
                    label: t("special"),
                    Icon: HiOutlineSparkles,
                  },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setFilterTab(tab.id)}
                    className={`px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 ${
                      filterTab === tab.id
                        ? "bg-orange-600 text-white"
                        : "text-white/90 hover:bg-white/10 dark:hover:bg-slate-700/50"
                    }`}
                  >
                    <tab.Icon className="w-4 h-4 shrink-0" />
                    {tab.label}
                  </button>
                ))}
              </div>
              {hasActiveFilter && (
                <span className="text-sm text-white/80">
                  {selectedCategory &&
                    `${t("categories")}: ${translatedCategoriesMap[selectedCategory] ?? selectedCategory}`}
                  {selectedCountry &&
                    `${t("countries")}: ${translatedCuisineNames[selectedCountry] ?? translatedCountriesMap[selectedCountry] ?? selectedCountry}`}
                </span>
              )}
            </div>

            <div className="max-h-56 overflow-y-auto pr-1 -mr-1">
              {filterTab === "categories" && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                  {categories.slice(0, 10).map((category) => (
                    <button
                      key={category.idCategory}
                      onClick={() => handleCategoryFilter(category.strCategory)}
                      className={`text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedCategory === category.strCategory
                          ? "bg-orange-600 text-white"
                          : "bg-white/20 dark:bg-white/10 text-white hover:bg-white/30 dark:hover:bg-white/20"
                      }`}
                    >
                      {translatedCategoriesMap[category.strCategory] ??
                        translateCategory(category.strCategory)}
                    </button>
                  ))}
                </div>
              )}
              {filterTab === "countries" && (
                <div className="space-y-3">
                  {/* Boutons de filtres par grande région */}
                  {availableRegions.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-1">
                      <button
                        type="button"
                        onClick={() => setSelectedRegion("")}
                        className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium border transition-colors ${
                          !selectedRegion
                            ? "bg-orange-600 text-white border-orange-500"
                            : "bg-white/10 text-white border-white/25 hover:bg-white/20"
                        }`}
                      >
                        {t("allRegions") || "Tous les continents"}
                      </button>
                      {availableRegions.map((region) => (
                        <button
                          key={region}
                          type="button"
                          onClick={() => setSelectedRegion(region)}
                          className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium border transition-colors ${
                            selectedRegion === region
                              ? "bg-orange-600 text-white border-orange-500"
                              : "bg-white/10 text-white border-white/25 hover:bg-white/20"
                          }`}
                        >
                          {regionLabels[region] || region}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Liste des pays (filtrés par région si sélectionnée) */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {regionFilteredCountries.map((country) => (
                      <button
                        key={country.strArea}
                        onClick={() => handleCountryFilter(country.strArea)}
                        className={`text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                          selectedCountry === country.strArea
                            ? "bg-orange-600 text-white"
                            : "bg-white/20 dark:bg-white/10 text-white hover:bg-white/30 dark:hover:bg-white/20"
                        }`}
                      >
                        <HiOutlineMapPin className="w-4 h-4 shrink-0" />
                        <span className="truncate">
                          {translatedCountriesMap[country.strArea] ??
                            country.strArea}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {filterTab === "special" && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {specialCuisines.map((cuisine) => (
                    <button
                      key={cuisine.name}
                      onClick={() => handleCountryFilter(cuisine.name)}
                      className={`text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                        selectedCountry === cuisine.name
                          ? "bg-orange-600 text-white"
                          : "bg-white/20 dark:bg-white/10 text-white hover:bg-white/30 dark:hover:bg-white/20"
                      }`}
                    >
                      <HiOutlineSparkles className="w-4 h-4 shrink-0" />
                      <span className="truncate">
                        {translatedCuisineNames[cuisine.name] ?? cuisine.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Grille de repas */}
          {loading ? (
            <CardGridLoading
              message={t("loadingMeals")}
              gridCols="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              count={8}
            />
          ) : meals.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {paginatedMeals.map((meal) => {
                  const isFavorite = !!favoriteMeals[meal.idMeal];
                  return (
                    <article
                      key={meal.idMeal}
                      className="group relative bg-white/10 backdrop-blur rounded-xl sm:rounded-2xl shadow-lg border border-slate-100 overflow-hidden cursor-pointer hover:shadow-xl hover:border-orange-300/50 transition-all duration-300 hover:-translate-y-1 flex flex-col h-full ring-2 ring-transparent hover:ring-orange-400/20"
                      onClick={() => handleMealClick(meal.idMeal)}
                    >
                      <div className="relative aspect-square rounded-lg sm:rounded-xl overflow-hidden mb-2 sm:mb-3 bg-gradient-to-br from-slate-200 via-slate-100 to-slate-200 shadow-inner ring-1 ring-black/10 flex-shrink-0">
                        <img
                          src={meal.strMealThumb}
                          alt={meal.strMeal}
                          className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700 ease-out"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-60 group-hover:opacity-0 transition-opacity duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/0 to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-orange-400/80 to-transparent opacity-0 group-hover:opacity-100 transform scale-x-0 group-hover:scale-x-100 transition-all duration-500 origin-center" />

                        <div className="absolute bottom-2 left-2 right-2 flex items-center gap-2 text-white drop-shadow z-10">
                          <HiOutlineMapPin className="w-4 h-4 shrink-0 opacity-90" />
                          <span className="text-xs font-medium truncate">
                            {selectedCountry &&
                            specialCuisines.find(
                              (c) => c.name === selectedCountry,
                            )
                              ? (translatedCuisineNames[selectedCountry] ??
                                selectedCountry)
                              : (translatedMealsMap[meal.idMeal]?.strArea ??
                                meal.strArea ??
                                t("international"))}
                          </span>
                        </div>
                      </div>
                      <div className="p-3 sm:p-4 flex flex-col flex-1">
                        <h3 className="font-bold text-white uppercase text-xs sm:text-sm mb-1 line-clamp-2 flex-shrink-0">
                          {translatedMealsMap[meal.idMeal]?.strMeal ??
                            translateRecipeName(meal.strMeal)}
                        </h3>
                        <span className="inline-block px-2.5 py-0.5 bg-orange-100/90 text-orange-800 text-xs font-semibold rounded-full w-fit mb-2">
                          {translatedMealsMap[meal.idMeal]?.strCategory ??
                            translateCategory(meal.strCategory) ??
                            t("miscellaneous")}
                        </span>
                        <div className="mt-auto flex items-center justify-between pt-1">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavoriteMeal(meal);
                            }}
                            className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-white transition-all duration-200 ${
                              isFavorite
                                ? "bg-red-500/90 hover:bg-red-600"
                                : "bg-white/20 hover:bg-white/30"
                            }`}
                            title={
                              isFavorite
                                ? t("removeFromFavorites") ||
                                  "Retirer des favoris"
                                : t("addToFavorites") || "Ajouter aux favoris"
                            }
                          >
                            {isFavorite ? (
                              <HiHeart className="w-4 h-4" />
                            ) : (
                              <HiOutlineHeart className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadMealPdf(meal);
                            }}
                            disabled={pdfLoading}
                            className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/15 hover:bg-white/25 text-white border border-white/30 disabled:opacity-60 flex items-center justify-center"
                            title={
                              t("downloadPdf") || "Télécharger la fiche PDF"
                            }
                          >
                            <FiDownload className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              {/* Pagination - même style que la page Recettes */}
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                >
                  <HiChevronLeft className="w-5 h-5" />
                  <span>{t("previous") || "Précédent"}</span>
                </button>
                <span className="px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white text-sm sm:text-base font-medium min-w-[7rem] text-center">
                  {t("page") || "Page"} {currentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                >
                  <span>{t("next") || "Suivant"}</span>
                  <HiChevronRight className="w-5 h-5" />
                </button>
              </div>
              {meals.length > 0 && (
                <p className="mt-3 text-center text-white/70 text-sm">
                  {meals.length} {t("recipes") || "recettes"}
                  {totalPages > 1
                    ? ` · ${t("page") || "Page"} ${currentPage}`
                    : ""}
                </p>
              )}
            </>
          ) : (
            <div className={`text-center py-12 ${GLASS_PANEL} rounded-2xl`}>
              <HiOutlineCircleStack className="w-14 h-14 mx-auto mb-4 text-white/70" />
              <h3 className="text-xl font-bold mb-2 text-white">
                {t("noResults")}
              </h3>
              <p className="text-sm mb-6 text-white/80">
                {t("tryOtherSearch")}
              </p>
              <button
                type="button"
                onClick={fetchRandomMeals}
                className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg transition-colors"
              >
                {t("discoverRandom")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal de détails - design premium */}
      {selectedMeal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-md overflow-y-auto"
          onClick={() => setSelectedMeal(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="meal-modal-title"
        >
          <div
            className="relative w-full max-w-4xl my-auto flex flex-col rounded-2xl sm:rounded-3xl overflow-hidden bg-white dark:bg-slate-900 shadow-2xl ring-1 ring-slate-700/50"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: "calc(100vh - 2rem)" }}
          >
            {/* Bande accent orange en haut */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 z-20" aria-hidden />

            <button
              type="button"
              onClick={() => setSelectedMeal(null)}
              className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/95 dark:bg-slate-800/95 shadow-lg flex items-center justify-center text-slate-600 dark:text-slate-300 hover:bg-red-50 dark:hover:bg-red-950/50 hover:text-red-600 dark:hover:text-red-400 transition-colors touch-manipulation"
              aria-label="Fermer"
            >
              <HiOutlineXMark className="w-6 h-6" />
            </button>

            {/* En-tête avec image */}
            <div className="relative h-52 sm:h-64 overflow-hidden flex-shrink-0">
              <img
                src={selectedMeal.strMealThumb}
                alt={selectedMeal.strMeal}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 text-white">
                <h2
                  id="meal-modal-title"
                  className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight drop-shadow-lg leading-tight break-words pr-12"
                >
                  {translatedSelected?.strMeal ??
                    translateRecipeName(selectedMeal.strMeal)}
                </h2>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/20 backdrop-blur-sm text-sm font-medium">
                    <HiOutlineGlobeAlt className="w-4 h-4 text-amber-300" />
                    {translatedSelected?.strArea ?? selectedMeal.strArea}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-500/90 text-sm font-semibold">
                    {translatedSelected?.strCategory ??
                      translateCategory(selectedMeal.strCategory)}
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {selectedMeal.strYoutube && (
                    <a
                      href={selectedMeal.strYoutube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold shadow-lg transition-colors"
                    >
                      <HiOutlinePlayCircle className="w-4 h-4" />
                      {t("watchVideo")}
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={handleDownloadSelectedPdf}
                    disabled={pdfLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold shadow-lg disabled:opacity-60 transition-colors"
                  >
                    <HiOutlineDocumentText className="w-4 h-4" />
                    {t("downloadPdf")}
                  </button>
                </div>
              </div>
            </div>

            {/* Contenu scrollable */}
            <div className="relative flex-1 min-h-0 overflow-y-auto bg-slate-50 dark:bg-slate-900/50">
              {translatingModal && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-slate-900/95 backdrop-blur-sm">
                  <div className="w-10 h-10 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
                  <p className="text-white font-medium">{t("translating")}</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] lg:grid-cols-[1.2fr_300px] gap-0">
                {/* Instructions - étapes numérotées */}
                <div className="p-5 sm:p-6 md:p-8 md:border-r border-slate-200 dark:border-slate-700/70">
                  <h3 className="flex items-center gap-2 text-lg font-bold text-slate-800 dark:text-slate-100 mb-4 pb-2 border-b-2 border-orange-500/30 w-fit">
                    <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-orange-500/20 dark:bg-orange-500/30 text-orange-600 dark:text-orange-400 font-bold text-sm">
                      1
                    </span>
                    {t("instructions")}
                  </h3>
                  <ol className="space-y-4">
                    {(
                      translatedSelected?.strInstructions ??
                      translateInstructions(selectedMeal.strInstructions || "") ??
                      ""
                    )
                      .split("\n")
                      .map((line) => line.trim())
                      .filter(Boolean)
                      .map((line, idx) => (
                        <li key={idx} className="flex gap-3">
                          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400 text-sm font-bold flex items-center justify-center mt-0.5">
                            {idx + 1}
                          </span>
                          <span className="text-slate-700 dark:text-slate-200 text-sm sm:text-base leading-relaxed">
                            {line}
                          </span>
                        </li>
                      ))}
                  </ol>
                </div>

                {/* Ingrédients - sidebar sticky sur desktop */}
                <div className="p-5 sm:p-6 md:p-8 bg-white dark:bg-slate-900/80 md:sticky md:top-0 md:self-start">
                  <h3 className="flex items-center gap-2 text-lg font-bold text-slate-800 dark:text-slate-100 mb-4 pb-2 border-b-2 border-orange-500/30 w-fit">
                    <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-orange-500/20 dark:bg-orange-500/30 text-orange-600 dark:text-orange-400 font-bold text-sm">
                      2
                    </span>
                    {t("ingredients")}
                  </h3>
                  <ul className="space-y-2">
                    {translatedSelected?.ingredients
                      ? translatedSelected.ingredients.map((ing, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-3 p-3 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/60"
                          >
                            <HiCheck className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
                            <span className="text-sm sm:text-base text-slate-700 dark:text-slate-200">
                              <span className="font-semibold text-slate-800 dark:text-slate-100">
                                {ing.measure}
                              </span>{" "}
                              {ing.name}
                            </span>
                          </li>
                        ))
                      : Array.from({ length: 20 }, (_, i) => i + 1).map((i) => {
                          const ingredient = selectedMeal[`strIngredient${i}`];
                          const measure = selectedMeal[`strMeasure${i}`];
                          if (ingredient && ingredient.trim()) {
                            return (
                              <li
                                key={i}
                                className="flex items-start gap-3 p-3 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/60"
                              >
                                <HiCheck className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
                                <span className="text-sm sm:text-base text-slate-700 dark:text-slate-200">
                                  <span className="font-semibold text-slate-800 dark:text-slate-100">
                                    {measure}
                                  </span>{" "}
                                  {translateIngredient(ingredient)}
                                </span>
                              </li>
                            );
                          }
                          return null;
                        })}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorldMenus;
